// @flow

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import {GetApp} from '@material-ui/icons';
import {useDebounce} from '../../../../utils/react';
import {Divider, Grid, makeStyles, Typography} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';

import RowItemProducts from './RowItem/RowItemProducts';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import LessStockProductsModal from '../Modals/LessStockProductsModal';

import {endpoints} from '../../api';
import type {ProductLessStockData, DashboardAction} from '../../duck';

type Props = {
  fetchLessStockProducts: (payload: DashboardAction) => void,
  lessStockProducts?: ProductLessStockData[],
  isFetchingFlaglessProducts?: boolean,
  flagLessStockProducts?: boolean,
};

const useStyles = makeStyles(theme => ({
  section: {
    height: '100%',
    backgroundColor: '#FFFFFF',
    padding: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
  },
  text: {
    color: colors.grey700,
    fontWeight: 500,
    fontSize: 12,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.grey900,
  },
  divider: {
    backgroundColor: '#E8E8ED',
    margin: theme.spacing(2, 0),
  },
  btnDownload: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  seeMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  seeMore: {
    color: colors.grey800,
    fontSize: 14,
  },
  inputStock: {
    width: 70,
  },
  progress: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  advice: {
    fontSize: 14,
    margin: theme.spacing(1),
    color: colors.grey700,
  },
  button: {
    borderRadius: '0px',
    width: '100%',
  },
}));

const mockData = [
  {
    name: 'Nombre Producto',
    stock: '1',
  },
  {
    name: 'Nombre Producto',
    stock: '1',
  },
  {
    name: 'Nombre Producto',
    stock: '1',
  },
];

export default function ProductsLessStockSection(props: Props) {
  const {
    lessStockProducts,
    fetchLessStockProducts,
    isFetchingFlaglessProducts,
    flagLessStockProducts,
  } = props;

  const classes = useStyles();
  const [openModal, setOpenModal] = useState(false);
  const [minStock, setMinStock] = useState('50');
  const debouncedMinStock = useDebounce(minStock, 500);

  const handleSeeMoreProducts = (v: boolean) => {
    setOpenModal(true);
  };

  const buildQueryParams = useCallback(() => {
    let params: {[key: string]: any} = {size: 10, page: 0};
    if (debouncedMinStock) {
      params.stock = debouncedMinStock;
    }
    return params;
  }, [debouncedMinStock]);

  useEffect(() => {
    if (!isFetchingFlaglessProducts && flagLessStockProducts) {
      const queryFilter = buildQueryParams();
      fetchLessStockProducts({queryFilter});
    }
  }, [
    buildQueryParams,
    fetchLessStockProducts,
    flagLessStockProducts,
    isFetchingFlaglessProducts,
  ]);

  const fileDownloadUrl = useMemo(() => {
    const queryParams = {stock: minStock, format: 'xlsx'};
    return `${
      endpoints.GET_SUMMARY_LESS_STOCK_PRODUCTS
    }?${queryString.stringify(queryParams)}`;
  }, [minStock]);

  return (
    <div className={classes.section}>
      <div>
        <div className={classes.header}>
          <Typography className={classes.title}>
            Productos con menos stock
          </Typography>
          <div>
            <CustomButton
              startIcon={<GetApp />}
              className={classes.btnDownload}
              href={fileDownloadUrl}
              variant="contained"
              target="_blank"
              rel="noopener noreferrer"
            >
              Descargar
            </CustomButton>
          </div>
        </div>
        <Divider className={classes.divider} />
        <div style={{height: 64, display: 'flex', alignItems: 'center'}}>
          <Typography className={classes.text}>
            Mostrar productos con menos de
          </Typography>
          <form style={{margin: '0 8px'}} onSubmit={e => e.preventDefault()}>
            <CustomTextField
              className={classes.inputStock}
              value={minStock}
              onChange={e => {
                const stockNumber = Number(e.target.value);

                if (isNaN(stockNumber) || stockNumber < 0) {
                  setMinStock('0');
                  return;
                }

                setMinStock(stockNumber.toString());
              }}
            />
          </form>
          <Typography className={classes.text}>unidades</Typography>
        </div>
        <div className={classes.wrapProducts}>
          <Grid container spacing={2}>
            {lessStockProducts && lessStockProducts.length ? (
              lessStockProducts
                .slice(0, 5)
                .map((p: ProductLessStockData, idx) => {
                  return (
                    <Grid item xs={12} key={idx}>
                      <CustomButton
                        className={classes.button}
                        to={`/products/${p.productId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <RowItemProducts
                          id={idx}
                          text="disponibles"
                          name={p.name}
                          imageId={p.imageId}
                          number={p.stock}
                        />
                      </CustomButton>
                    </Grid>
                  );
                })
            ) : !flagLessStockProducts ? (
              mockData.map((p, idx) => (
                <Grid item xs={12} key={idx}>
                  <RowItemProducts
                    id={idx}
                    text="disponibles"
                    name={p.name}
                    number={p.stock}
                  />
                </Grid>
              ))
            ) : (
              <Typography className={classes.advice}>
                No tienes productos con stock mínimo de {minStock}.
              </Typography>
            )}
          </Grid>
        </div>
        <Divider className={classes.divider} />
        {lessStockProducts && lessStockProducts.length > 5 ? (
          <div className={classes.seeMoreContainer}>
            <CustomButton
              className={classes.seeMore}
              onClick={handleSeeMoreProducts}
            >
              {openModal ? 'Ver menos' : 'Ver más'}
            </CustomButton>
          </div>
        ) : null}
        <LessStockProductsModal
          open={openModal}
          setOpen={setOpenModal}
          fetchLessStockProducts={fetchLessStockProducts}
        />
      </div>
    </div>
  );
}

ProductsLessStockSection.propTypes = {
  fetchLessStockProducts: PropTypes.func.isRequired,
  lessStockProducts: PropTypes.array,
  isFetchingFlaglessProducts: PropTypes.bool,
  flagLessStockProducts: PropTypes.bool,
};
